import { Avatar, Badge, Button, Checkbox, Flex, message, Spin, theme, Typography } from 'antd'
import {
  ClockCircleOutlined,
  PoweroffOutlined,
  PrinterOutlined,
  UserOutlined,
} from '@ant-design/icons'
import React, { memo, useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { compareWithJSON, generateCardName, getColorForProduct } from 'utils/helper'
import { currency, formatDate } from 'utils/formatting'
import SalesSummary from 'components/apps/Reports/PosShift/Detail/SalesSummary'
import PaymentMethod from 'components/apps/Reports/PosShift/Detail/PaymentMethod'
import CashMovement from 'components/apps/Reports/PosShift/Detail/CashMovement'
import CashReconciliation from 'components/apps/Reports/PosShift/Detail/CashReconciliation'
import ProductsSold from 'components/apps/Reports/PosShift/Detail/ProductsSold'
import UnfinishedTransactions from 'components/apps/Reports/PosShift/Detail/UnfinishedTransactions'
import VoidedPaidInvoices from 'components/apps/Reports/PosShift/Detail/VoidedPaidInvoices'
import { useSimplePrint } from '@/utils/hooks/useThermalPrinter'
import colorPalette from '@/components/apps/FinanceAccounts/Charts/colors'
import { useShift } from '@/utils/queries/shift'
import { userSelector } from '@/redux/selectors'
import useMemoizePermissions from '@/utils/hooks/useMemoizePermissions'
import CloseShift from '@/components/apps/Cashier/CloseShift'
import ShiftStatusPill from './ShiftStatusPill'
import LiveDuration from './LiveDuration'

const { Text } = Typography

const ShiftHistoryDetail = ({ data = {}, title }) => {
  const { formatMessage } = useIntl()
  const { token } = theme.useToken()
  const { printShiftDetail, isLoading: isPrinting } = useSimplePrint()

  const [printProduct, setPrintProduct] = useState(true)
  const [printPaymentDetail, setPrintPaymentDetail] = useState(false)
  const [closeShiftOpen, setCloseShiftOpen] = useState(false)

  const user = useSelector(userSelector)
  const permissions = useMemoizePermissions({ resource: 'pos', recursive: true })
  const isCanCloseOtherShift = !!permissions.pos_setting_cashiers_close_other_shift

  const { data: shiftRes, isLoading: isDetailLoading } = useShift(data.id, {
    enabled: !!data.id,
  })

  const detailData = useMemo(() => ({ ...data, ...(shiftRes?.data?.data || {}) }), [data, shiftRes])

  const userName = detailData.user?.name || '-'
  const userPhoto = detailData.user?.profile_image
  const userInitials = generateCardName(userName)
  const userColors = getColorForProduct(userName, colorPalette)
  const startTime = detailData.start_time
  const isOpen = !!detailData.id && !detailData.end_time

  // Shift open bisa ditutup oleh pemiliknya, atau kasir lain yang punya izin.
  const canCloseShift = useMemo(() => {
    if (!isOpen) {
      return false
    }
    if (isCanCloseOtherShift) {
      return true
    }
    const ownerId = detailData.user_id ?? detailData.user?.id
    return ownerId != null && String(ownerId) === String(user?.id ?? '')
  }, [isOpen, isCanCloseOtherShift, detailData.user_id, detailData.user?.id, user?.id])

  const closeShiftTarget = useMemo(
    () => ({
      id: detailData.id,
      start_time: detailData.start_time,
      user: {
        id: detailData.user_id ?? detailData.user?.id,
        name: detailData.user?.name,
        profile_image: detailData.user?.profile_image,
      },
    }),
    [
      detailData.id,
      detailData.start_time,
      detailData.user_id,
      detailData.user?.id,
      detailData.user?.name,
      detailData.user?.profile_image,
    ],
  )

  const handleOpenCloseShift = useCallback(() => {
    setCloseShiftOpen(true)
  }, [])

  const handleCancelCloseShift = useCallback(() => {
    setCloseShiftOpen(false)
  }, [])

  const handlePrint = useCallback(async () => {
    try {
      const result = await printShiftDetail(detailData, {
        withProduct: printProduct,
        withPaymentDetail: printPaymentDetail,
      })
      if (result.success) {
        message.success(result.message)
      } else {
        message.error(result.message)
      }
    } catch (error) {
      message.error(error?.message || 'Print failed')
    }
  }, [detailData, printShiftDetail, printProduct, printPaymentDetail])

  return (
    <Flex
      vertical
      gap={0}
      style={{
        height: 'calc(100vh - 18px)',
        minHeight: 0,
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          paddingTop: 16,
          paddingLeft: 16,
          paddingRight: 16,
        }}
      >
        <Spin spinning={isDetailLoading}>
          {/* Header: Avatar, Kasir, Tanggal Buka | Title, No Shift */}
          <Flex justify="space-between" align="flex-start" style={{ marginBottom: 24 }}>
            <Flex gap={16} align="center">
              <Avatar
                size={56}
                src={userPhoto}
                icon={!userPhoto ? <UserOutlined /> : null}
                style={{
                  backgroundColor: userPhoto ? 'transparent' : userColors.bg,
                  color: userColors.text,
                  fontSize: 20,
                  fontWeight: 600,
                }}
              >
                {!userPhoto ? userInitials : null}
              </Avatar>
              <Flex vertical gap={4}>
                <Text strong style={{ fontSize: 18 }}>
                  {userName}
                </Text>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  <ClockCircleOutlined style={{ marginRight: 6 }} />
                  {startTime ? formatDate(startTime, 'DD MMM YYYY, HH:mm') : '-'}
                </Text>
              </Flex>
            </Flex>
            <Flex vertical gap={8} align="flex-end">
              <Flex gap={8} align="center">
                {title && (
                  <Text strong style={{ fontSize: 14 }}>
                    {title}
                  </Text>
                )}
                {!!detailData.id && <ShiftStatusPill isOpen={isOpen} />}
              </Flex>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {formatMessage({ id: 'pos.shift_id' })} #{detailData.id || '-'}
              </Text>
            </Flex>
          </Flex>

          {/* Banner shift yang masih berjalan */}
          {isOpen && (
            <Flex
              justify="space-between"
              align="center"
              gap={16}
              wrap="wrap"
              style={{
                background: token.colorSuccessBg,
                border: `1px solid ${token.colorSuccessBorder}`,
                borderRadius: 10,
                padding: '12px 16px',
                marginBottom: 16,
              }}
            >
              <Flex align="center" gap={12} style={{ minWidth: 0 }}>
                <Badge status="processing" color={token.colorSuccess} />
                <Flex vertical gap={2} style={{ minWidth: 0 }}>
                  <Text strong style={{ color: token.colorSuccessText }}>
                    <FormattedMessage
                      id="shift_running_since"
                      values={{
                        duration: <LiveDuration start={startTime} />,
                        time: startTime ? formatDate(startTime, 'DD MMM YYYY, HH:mm') : '-',
                      }}
                    />
                  </Text>
                  {!canCloseShift && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatMessage({ id: 'shift_close_owner_only' })}
                    </Text>
                  )}
                </Flex>
              </Flex>
              {canCloseShift && (
                <Button
                  type="primary"
                  icon={<PoweroffOutlined />}
                  onClick={handleOpenCloseShift}
                  style={{ borderRadius: 8 }}
                >
                  {formatMessage({ id: 'close_shift' })}
                </Button>
              )}
            </Flex>
          )}

          {/* Transaction Details: Outlet, Buka, Tutup */}
          <div
            style={{
              backgroundColor: token.colorFillAlter,
              borderRadius: 8,
              padding: '16px 20px',
              marginBottom: 24,
            }}
          >
            <Flex justify="space-around" wrap="wrap" gap={16}>
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                  {formatMessage({ id: 'outlet_name' })}
                </Text>
                <Text strong>{detailData.outlet?.name || '-'}</Text>
              </Flex>
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                  {formatMessage({ id: 'report.open_time' })}
                </Text>
                <Text strong>
                  {detailData.start_time
                    ? formatDate(detailData.start_time, 'DD MMM YYYY, HH:mm')
                    : '-'}
                </Text>
              </Flex>
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                  {formatMessage({ id: 'report.close_time' })}
                </Text>
                <Text strong>
                  {detailData.end_time
                    ? formatDate(detailData.end_time, 'DD MMM YYYY, HH:mm')
                    : '-'}
                </Text>
              </Flex>
            </Flex>
          </div>

          <SalesSummary data={detailData} />
          <div style={{ marginTop: 16 }}>
            <UnfinishedTransactions data={detailData} />
          </div>
          <div style={{ marginTop: 16 }}>
            <PaymentMethod data={detailData} />
          </div>
          <div style={{ marginTop: 16 }}>
            <VoidedPaidInvoices data={detailData} />
          </div>
          <div style={{ marginTop: 16 }}>
            <CashMovement data={detailData} />
          </div>
          <div style={{ marginTop: 16 }}>
            <CashReconciliation data={detailData} />
          </div>
          <div style={{ marginTop: 16 }}>
            <ProductsSold
              data={detailData?.items}
              productCategories={detailData?.product_categories}
            />
          </div>

          {/* Expected Total */}
          <Flex
            justify="space-between"
            align="center"
            style={{
              paddingTop: 16,
              marginTop: 16,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <Text strong>{formatMessage({ id: 'expected_total' })}</Text>
            <Text strong style={{ fontSize: 22, color: '#4677f2' }}>
              {currency(detailData.system_balance || 0)}
            </Text>
          </Flex>
        </Spin>
      </div>

      {/* Footer - Print */}
      <div
        style={{
          flexShrink: 0,
          width: '100%',
          marginTop: 16,
          backgroundColor: token.colorFillAlter,
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          paddingTop: 12,
          paddingBottom: 18,
          paddingLeft: 16,
          paddingRight: 16,
        }}
      >
        <Flex justify="space-between" align="center" gap={16}>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            loading={isPrinting}
            style={{ borderRadius: 8 }}
          >
            {formatMessage({ id: 'print_recap' })}
          </Button>
          <Flex vertical gap={4} align="flex-start">
            <Checkbox checked={printProduct} onChange={(e) => setPrintProduct(e.target.checked)}>
              {formatMessage({ id: 'print_product_sold' })}
            </Checkbox>
            <Checkbox
              checked={printPaymentDetail}
              onChange={(e) => setPrintPaymentDetail(e.target.checked)}
            >
              {formatMessage({ id: 'print_payment_detail' })}
            </Checkbox>
          </Flex>
        </Flex>
      </div>
      {closeShiftOpen && (
        <CloseShift open shift={closeShiftTarget} onCancel={handleCancelCloseShift} />
      )}
    </Flex>
  )
}

export default memo(ShiftHistoryDetail, compareWithJSON)
